/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useState } from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { Box, Flex, Pressable, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { CopyButton } from './copy-button';
import debugMessages from './messages';

const styles = cssMap({
	container: {
		marginBlockStart: token('space.050'),
	},
	header: {
		cursor: 'pointer',
		paddingBlock: token('space.050'),
	},
	label: {
		font: token('font.body.small'),
		fontWeight: token('font.weight.medium'),
		color: token('color.text.subtlest'),
	},
	thinkingBlock: {
		font: token('font.body.small'),
		fontFamily: token('font.family.code'),
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-word',
		paddingBlock: token('space.100'),
		paddingInline: token('space.100'),
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.small'),
		marginBlockStart: token('space.050'),
		marginBlockEnd: token('space.0'),
		marginInlineStart: token('space.0'),
		marginInlineEnd: token('space.0'),
		maxHeight: '400px',
		overflow: 'auto',
	},
});

interface DebugThinkingPanelProps {
	/** AI reasoning chain text */
	thinking: string;
}

/**
 * Insight-level debug panel showing the AI's reasoning chain.
 * Only rendered when `thinking` is available.
 */
export const DebugThinkingPanel = ({ thinking }: DebugThinkingPanelProps): JSX.Element => {
	const [expanded, setExpanded] = useState(false);
	const toggle = useCallback(() => setExpanded((prev) => !prev), []);
	const { formatMessage } = useIntl();

	return (
		<Box xcss={styles.container}>
			<Pressable onClick={toggle} xcss={styles.header}>
				<Flex alignItems="center" gap="space.050">
					{expanded ? (
						<ChevronDownIcon
							label={formatMessage(debugMessages.collapseLabel)}
							color={token('color.icon')}
						/>
					) : (
						<ChevronRightIcon
							label={formatMessage(debugMessages.expandLabel)}
							color={token('color.icon')}
						/>
					)}
					<Box as="span" xcss={styles.label}>
						{formatMessage(debugMessages.aiThinking)}
					</Box>
				</Flex>
			</Pressable>
			{expanded && (
				<Stack space="space.050">
					<CopyButton text={thinking} label={formatMessage(debugMessages.copyLabel)} />
					<pre css={styles.thinkingBlock}>{thinking}</pre>
				</Stack>
			)}
		</Box>
	);
};
